import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { USED_ASSESSMENT_NAME_ERROR } from "@/constants/knowledgeAssessments";

interface MetadataValues {
  title?: string;
  description?: string;
}

interface ValidationError {
  hasError?: boolean;
  message?: string;
}

interface AssessmentFormMetadataProps {
  values: MetadataValues;
  handleChange: (values: MetadataValues) => void;
  isAssessmentTitleUsed: boolean;
  showValidationMessages: boolean;
  validationErrors?: {
    name?: ValidationError;
    description?: ValidationError;
  };
}

export default function AssessmentFormMetadata({
  values,
  handleChange,
  isAssessmentTitleUsed,
  showValidationMessages,
  validationErrors,
}: AssessmentFormMetadataProps) {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    handleChange({ ...values, [name]: value });
  };

  const isNameInvalid =
    isAssessmentTitleUsed || (showValidationMessages && !!validationErrors?.name?.hasError);
  const nameErrorMessage = isAssessmentTitleUsed
    ? USED_ASSESSMENT_NAME_ERROR
    : validationErrors?.name?.message;
  const isDescriptionInvalid = showValidationMessages && !!validationErrors?.description?.hasError;

  return (
    <div className="flex flex-col gap-10">
      <div>
        <FloatLabel className="mt-10">
          <InputText
            id="knowledge-assessment-name"
            name="title"
            className="w-full"
            value={values.title ?? ""}
            onChange={handleInputChange}
            autoComplete="off"
            aria-required="true"
            aria-invalid={isNameInvalid}
            aria-describedby={isNameInvalid ? "assessment-name-error" : undefined}
            invalid={isNameInvalid}
          />
          <label htmlFor="knowledge-assessment-name">Assessment Name</label>
        </FloatLabel>
        {isNameInvalid && (
          <div className="mt-2" role="alert">
            <ValidationMessage
              severity="error"
              text={nameErrorMessage}
              id="assessment-name-error"
            />
          </div>
        )}
      </div>

      <div>
        <FloatLabel className="mt-12">
          <InputTextarea
            id="knowledge-assessment-description"
            name="description"
            className="w-full"
            value={values.description ?? ""}
            onChange={handleInputChange}
            rows={5}
            cols={30}
            autoComplete="off"
            aria-invalid={isDescriptionInvalid}
            aria-describedby={isDescriptionInvalid ? "assessment-description-error" : undefined}
            invalid={isDescriptionInvalid}
          />
          <label htmlFor="knowledge-assessment-description">Assessment Description</label>
        </FloatLabel>
        {isDescriptionInvalid && (
          <div role="alert">
            <ValidationMessage
              severity="error"
              text={validationErrors?.description?.message}
              id="assessment-description-error"
            />
          </div>
        )}
      </div>
    </div>
  );
}
